import { Divider, Stack, Text } from '@chakra-ui/react'
import { PopUpMessageContext, ThemeContext, useApp, useAuth } from '@hb/react'
import { FileDBValue, objectToArray, PracticeUserData, UserGroup } from '@hb/shared'
import { DocumentReference } from 'firebase/firestore'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useRefDocument } from '../../../../backend/db/hooks/useDocument'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useConsentFormsStatus } from '../../../../hooks'
import { ActionButton } from '../../../Buttons'
import { AssignFileAsConsentFormModal } from '../../../Files/AssignFileAsConsentFormModal'
import { FilesListViewWrapper } from '../../../Files/FilesListViewWrapper'
import { MoveFileModal } from '../../../Files/MoveFileModal'

const PatientFilesListView = ({
  docRef,
  files,
  access,
  preview,
  canMerge,
  adminView,
  patientId,
}: {
  docRef: DocumentReference
  files: Record<string, FileDBValue> | undefined
  access: UserGroup
  preview?: boolean
  canMerge?: boolean
  adminView: boolean
  patientId: string
}) => {
  const { processResponse } = useContext(PopUpMessageContext)
  const { appName } = useApp()
  const isAdmin = useAuth(s => s.admin)
  const [assignModalFile, setAssignModalFile] = useState<{
    file: FileDBValue
    fileId: string
  } | null>(null)
  const [moveModalFile, setMoveModalFile] = useState<{
    file: FileDBValue
    fileId: string
  } | null>(null)

  // Get consent forms status to check for unsigned forms
  const { consentForms } = useConsentFormsStatus(patientId, null, null)
  const unsignedConsentForms = useMemo(
    () => objectToArray(consentForms ?? {}).filter(form => !form.signedOn),
    [consentForms],
  )

  const onCombineFiles = useCallback(
    async (fileIds: string[]) => {
      if (fileIds.length < 2) {
        processResponse({ error: 'Select at least 2 files to merge' })
        return
      }
      // Placeholder for backend implementation
      // When implemented, use: const filePrefix = userGroupToFileIdPrefix(access)
      processResponse({
        error: 'Patient file merging coming soon. Backend implementation pending.',
      })
    },
    [processResponse],
  )

  const canEdit = useMemo(() => {
    if (appName === 'providers-app') {
      return access === 'practice'
    }
    return access === 'admin'
  }, [access, appName])

  const getFileHeader = useCallback(
    (fileId: string, file: FileDBValue): React.ReactNode => {
      const buttons: React.ReactNode[] = []

      // Move file button - show for admins
      if (adminView && isAdmin && appName === 'app') {
        buttons.push(
          <ActionButton
            key="move"
            size="xs"
            onClick={e => {
              e.stopPropagation()
              setMoveModalFile({ file, fileId })
            }}>
            <Text fontSize="xs">Move File</Text>
          </ActionButton>,
        )
      }

      // Assign as consent form button - only for admins, PDF files, and when unsigned consent forms exist
      if (
        adminView &&
        isAdmin &&
        file.type === 'application/pdf' &&
        unsignedConsentForms.length > 0
      ) {
        buttons.push(
          <ActionButton
            key="assign"
            size="xs"
            onClick={e => {
              e.stopPropagation()
              setAssignModalFile({ file, fileId })
            }}>
            <Text fontSize="xs">Assign as Consent Form</Text>
          </ActionButton>,
        )
      }

      return buttons.length > 0 ? <>{buttons}</> : null
    },
    [adminView, isAdmin, unsignedConsentForms.length, appName],
  )

  return (
    <>
      <FilesListViewWrapper
        adminView={adminView}
        docRef={docRef}
        files={files}
        access={access}
        preview={preview}
        canMerge={canMerge}
        canEdit={canEdit}
        onCombineFiles={onCombineFiles}
        getFileHeader={getFileHeader}
      />
      {assignModalFile ? (
        <AssignFileAsConsentFormModal
          isOpen={!!assignModalFile}
          fileId={assignModalFile.fileId}
          onClose={() => setAssignModalFile(null)}
          file={assignModalFile.file}
          patientId={patientId}
          accessLevel={access}
        />
      ) : null}
      {moveModalFile ? (
        <MoveFileModal
          isOpen={!!moveModalFile}
          fileId={moveModalFile.fileId}
          onClose={() => setMoveModalFile(null)}
          file={moveModalFile.file}
          sourceType="patient"
          patientId={patientId}
          sourceAccessLevel={access}
          sourceAssessmentId={null}
          sourcePracticeId={null}
        />
      ) : null}
    </>
  )
}

export const PatientFiles = ({ preview }: { preview?: boolean } = {}) => {
  const { user, userId, patientRef, adminPatientRef, practicePatientRef, adminItem } = useProfile()
  const { appName } = useApp()

  // Fetch admin and practice files from their respective documents
  const { data: practiceUserData } = useRefDocument<
    PracticeUserData & { files?: Record<string, FileDBValue> }
  >(practicePatientRef)

  const practiceFiles = practiceUserData?.files
  const patientLevelFiles = user?.files

  return (
    <ThemeContext.Provider value={{ placeholderAbove: false }}>
      <Stack divider={preview ? <Divider /> : undefined} spacing={preview ? 0 : 4}>
        {/* Admin Files */}
        {appName === 'app' && adminPatientRef && userId ? (
          <PatientFilesListView
            docRef={adminPatientRef}
            files={adminItem?.files}
            access="admin"
            preview={preview}
            canMerge={!preview && appName === 'app'}
            adminView
            patientId={userId}
          />
        ) : null}
        {/* Practice Files */}
        {practicePatientRef && userId ? (
          <PatientFilesListView
            docRef={practicePatientRef}
            files={practiceFiles}
            access="practice"
            preview={preview}
            canMerge={!preview && appName === 'app'}
            adminView
            patientId={userId}
          />
        ) : null}
        {/* Patient Files */}
        {userId && patientRef ? (
          <PatientFilesListView
            docRef={patientRef}
            files={patientLevelFiles}
            access="patient"
            preview={preview}
            canMerge={!preview && appName === 'app'}
            adminView
            patientId={userId}
          />
        ) : null}
      </Stack>
    </ThemeContext.Provider>
  )
}
